// angular
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, inject, input, OnChanges } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ICardProperty } from '@app/core/components/card-property/card-property.component';
import { CardTitleComponent } from '@app/core/components/card-title/card-title.component';
import { TextSummarizerComponent } from '../text-summarizer/text-summarizer.component';
import { ConfirmationDialogComponent } from '@app/core/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ItemPricesComponent } from '../item-prices/item-prices.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
import { FavoritesService } from '@app/+profile/profile-favorites/shared/favorites.service';
import { StorageService } from '@app/shared/services/storage.service';
// interfaces
import {
  IProduct,
  PriceType,
} from '@app/+products/shared/interfaces/product.interface';
import { ICardTitle } from '@app/shared/interfaces/card-title.interface';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { CurrencyCodes } from '@app/shared/enums/currencies.enum';
// pipes & environment
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { environment } from '@environments/environment';

@Component({
  selector: 'pwj-product-card',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    CardTitleComponent,
    TranslateModule,
    TextSummarizerComponent,
    TranslateDataPipe,
    ImageSourcePipe,
    ItemPricesComponent,
    RouterLink,
  ],
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.scss',
})
export class ProductCardComponent implements OnChanges {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public baseUrl: string = environment.baseUrl.base;
  public cardTitleData!: ICardTitle;
  public currentCurrency: keyof PriceType = CurrencyCodes.usd;

  // card properties
  public cardProperties!: ICardProperty[];

  // inputs
  productCardData = input.required<IProduct>();
  simpleBorder = input<boolean>(false);
  isFavoriteMode = input<boolean>(false);

  // dependencies
  private readonly _dialogService = inject(DialogService);
  private readonly _favoriteService = inject(FavoritesService);
  private readonly _storageService = inject(StorageService);
  private readonly _router = inject(Router);

  constructor() {
    this.currentCurrency =
      this._storageService.getCurrency() as keyof PriceType;
  }

  ngOnChanges(): void {
    this.cardTitleData = {
      title: this.productCardData()?.title as II18n,
      titleClasses: '!text-4xl',
      summarizeTitle: true,
      summarizeTitleLength: 22,
      subTitle: this.productCardData()?.business?.title as II18n,
      subTitleUrl: this.productCardData()?.business?.custom_url
        ? `/${this.productCardData()?.business?.custom_url}`
        : null,
      subTitleClasses: '[&>span]:!text-3xl',
      summarizeSubTitle: true,
      summarizeSubTitleLength: 23,
      image: {
        src:
          this.productCardData()?.business?.logo?.cropped_url?.toLowerCase() ||
          this.productCardData()?.business?.logo?.url,
        alt: this.productCardData()?.business?.title,
        width: '32',
        height: '32',
      },
      imgClasses: '!w-32 !h-32',
    };
  }

  public removeProductFromFavorite(event: MouseEvent) {
    event.stopPropagation();

    const dialogRef = this._dialogService.open(ConfirmationDialogComponent, {
      width: '26em',
      height: '10em',
      data: { title: 'profile.favorites.confirmDeleteProductFromFavorites' },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && this.productCardData()?._id) {
        this._favoriteService.removeOneFavoriteItem(
          this.productCardData()?._id as string
        );
        // this.isFavoriteMode() = false;
      }
    });
  }
}
